<template>
    <v-container>
        <v-row>
            <v-col>
                <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
                <v-btn :to="backRoute" prepend-icon="mdi-arrow-left-circle" variant="tonal">Tilbake til listen</v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-card class="profile-whitelist-form">
                    <v-card-text>
                        <v-form ref="form" v-model="isFormValid" :class="{ loading: loading }" @submit.prevent="submit">
                            <div class="phone">
                                <select-item
                                    v-model="store.currentProfileWhitelist.phoneCountryCode"
                                    :items="phoneCountryCodes"
                                    :selected="store.currentProfileWhitelist.phoneCountryCode"
                                    label="Land"
                                />
                            </div>
                            <v-btn :disabled="submitDisabled" class="submit-button" color="primary" type="submit">Submit</v-btn>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue';
import { useProfileWhitelistStore } from './profileWhitelistStore';
import { useRouter } from 'vue-router';
import { ProfileWhitelistRels } from './rels';
import { AlertTypes, Events, useEmitter } from '@/features/shared/useEmitter';
import { getErrors } from '../shared/ErrorHelper';
import { PhoneCountryCode } from '@/features/shared/models';
import { useCommonStore } from '@/features/shared/commonStore';
import SelectItem from '@/features/shared/components/SelectItem.vue';

const store = useProfileWhitelistStore();
const router = useRouter();
const eventBus = useEmitter();
const commonStore = useCommonStore();

const isFormValid = ref<boolean | null>();
const form = ref<HTMLElement & { validate: () => Promise<{ valid: boolean; errors: any[] }> }>();
const loading = ref(false);
const backRoute = ref({ name: ProfileWhitelistRels.GetProfileWhitelists });
const breadcrumbs = [
    {
        title: 'Hviteliste',
        disabled: false,
        to: { name: ProfileWhitelistRels.GetProfileWhitelists }
    },
    {
        title: 'Ny hviteliste-oppføring (Landskode)'
    }
];

const phoneCountryCodes = ref<Array<PhoneCountryCode>>([]);

onMounted(async () => {
    store.clearCurrentProfileWhitelist();

    await store.loadProfileWhitelists();

    const allCountryCodes = await commonStore.getCountryPhoneCodes();
    const whitelistedCountryCodes = store.profileWhitelists.map(item => item.phoneCountryCode).filter(code => !!code);

    phoneCountryCodes.value = allCountryCodes.filter(code => !whitelistedCountryCodes.includes(code.code));

    if (phoneCountryCodes.value.length > 0) {
        store.currentProfileWhitelist.phoneCountryCode = phoneCountryCodes.value[0].code;
    }
});

const disabled = ref(false);

watch(isFormValid, newValue => {
    if (newValue === null) {
        disabled.value = false;
    } else {
        disabled.value = !newValue;
    }
});

const submitDisabled = ref(false);

async function submit() {
    loading.value = true;
    const validation = await form.value?.validate();
    if (!validation?.valid) return;

    try {
        await store.createProfileWhitelistByCountryCode();
        loading.value = false;
        const alertContent = { type: AlertTypes.success, text: 'Hviteliste-oppføring lagret' };
        eventBus.emitter.emit(Events.alert, alertContent);
        await router.push({ name: ProfileWhitelistRels.GetProfileWhitelists });
    } catch (err: any) {
        loading.value = false;
        const data = err.response.data;
        const errors = getErrors(data);

        if (errors !== null) {
            eventBus.emitter.emit(Events.alert, errors);
        } else {
            throw err;
        }
    }
}
</script>

<style scoped>
.profile-whitelist-form {
    width: 100%;
    max-width: 700px;
}

.loading {
    opacity: 0.5;
}

.phone {
    display: grid;
    grid-template-columns: 32% 68%;
}

.submit-button {
    margin-top: 10px;
}
</style>
